<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import DddAssignmentsAPI from 'dashboard/api/dddAssignments';
import SettingsHeader from 'dashboard/routes/dashboard/settings/SettingsHeader.vue';
import Spinner from 'shared/components/Spinner.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import teamsAPI from 'dashboard/api/teams';
import { createNoneAgent } from 'dashboard/helper/agentHelper';
import Button from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'ContactDddAssignments',
  components: {
    Button,
    SettingsHeader,
    Spinner,
    MultiselectDropdown,
  },
  data() {
    return {
      assignments: [],
      removedAssignmentIds: [],
      isFetching: false,
      isSaving: false,
      teams: [],
    };
  },
  computed: {
    ...mapGetters({
      agentsList: 'agents/getVerifiedAgents',
    }),
    noneOption() {
      return {
        ...createNoneAgent,
        id: null,
        name: this.$t('DDD_ASSIGNMENTS.NONE_OPTION'),
      };
    },
    agentOptions() {
      return [this.noneOption, ...this.agentsList];
    },
    teamOptions() {
      return [
        {
          id: null,
          name: this.$t('DDD_ASSIGNMENTS.NONE_OPTION_TARGET'),
        },
        ...this.teams,
      ];
    },
    targetOptions() {
      return [
        {
          id: 'agent',
          name: this.$t('DDD_ASSIGNMENTS.TARGET_AGENT'),
          value: 'agent',
        },
        {
          id: 'team',
          name: this.$t('DDD_ASSIGNMENTS.TARGET_TEAM'),
          value: 'team',
        },
      ];
    },
    backUrl() {
      return `/app/accounts/${this.$route.params.accountId}/contacts`;
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.fetchTeams();
    this.fetchAssignments();
  },
  methods: {
    async fetchTeams() {
      try {
        const response = await teamsAPI.get();
        this.teams = response.data || [];
      } catch (error) {
        // silently fail, user will still see agents
      }
    },
    async fetchAssignments() {
      this.isFetching = true;
      try {
        const response = await DddAssignmentsAPI.get();
        this.assignments = (response.data || []).map(item => ({
          id: item.id,
          ddd: item.ddd || '',
          agent_id: item.agent_id || null,
          team_id: item.team_id || null,
          target: item.team_id ? 'team' : item.agent_id ? 'agent' : null,
        }));
      } catch (error) {
        useAlert(this.$t('DDD_ASSIGNMENTS.LOAD_ERROR'));
      } finally {
        this.isFetching = false;
      }
    },
    addRow() {
      const hasBlankRow = this.assignments.some(
        a =>
          !a.ddd &&
          !a.target &&
          !a.agent_id &&
          !a.team_id &&
          !a.id
      );
      if (hasBlankRow) return;

      this.assignments.push({
        id: null,
        ddd: '',
        agent_id: null,
        team_id: null,
        target: null,
      });
    },
    removeRow(index) {
      const assignment = this.assignments[index];
      if (assignment?.id) {
        this.removedAssignmentIds.push(assignment.id);
      }
      this.assignments.splice(index, 1);
    },
    onDddInput(index, value) {
      const rawValue = value && value.target ? value.target.value : value;
      const normalized = this.normalizeDdd(rawValue);
      this.assignments.splice(index, 1, {
        ...this.assignments[index],
        ddd: normalized,
      });
    },
    onSelectTarget(index, target) {
      this.assignments.splice(index, 1, {
        ...this.assignments[index],
        target,
        agent_id: null,
        team_id: null,
      });
    },
    optionsForTarget(assignment) {
      return assignment.target === 'team' ? this.teamOptions : this.agentOptions;
    },
    selectedAssigneeFor(assignment) {
      if (assignment.target === 'team') {
        return this.teamOptions.find(item => item.id === assignment.team_id);
      }
      return this.agentOptions.find(item => item.id === assignment.agent_id);
    },
    selectedTargetFor(assignment) {
      return this.targetOptions.find(opt => opt.value === assignment.target);
    },
    placeholderForAssignee(assignment) {
      return assignment.target
        ? this.$t('DDD_ASSIGNMENTS.NONE_OPTION_TARGET')
        : this.$t('DDD_ASSIGNMENTS.CHOOSE_TARGET');
    },
    onSelectAssignee(index, assignee) {
      const assignment = this.assignments[index];
      if (assignment.target === 'team') {
        this.assignments.splice(index, 1, {
          ...assignment,
          team_id: assignee?.id || null,
          agent_id: null,
        });
      } else {
        this.assignments.splice(index, 1, {
          ...assignment,
          agent_id: assignee?.id || null,
          team_id: null,
        });
      }
    },
    onSelectAgent(index, selectedAgent) {
      const agentId = selectedAgent?.id || null;
      this.assignments.splice(index, 1, {
        ...this.assignments[index],
        agent_id: agentId,
      });
    },
    selectedAgentFor(assignment) {
      if (!assignment.agent_id) return this.noneOption;

      return (
        this.agentsList.find(agent => agent.id === assignment.agent_id) ||
        this.noneOption
      );
    },
    normalizeDdd(value) {
      if (value === null || value === undefined) return '';

      return value.toString().replace(/\D/g, '').slice(0, 2);
    },
    isValidDdd(value) {
      return /^\d{2}$/.test(value);
    },
    hasDuplicateDdds() {
      const seen = new Set();
      for (const assignment of this.assignments) {
        if (!assignment.ddd) continue;
        if (seen.has(assignment.ddd)) return true;
        seen.add(assignment.ddd);
      }
      return false;
    },
    async saveAssignments() {
      if (this.isSaving) return;

      const invalidAssignment = this.assignments.find(
        assignment => !this.isValidDdd(assignment.ddd)
      );
      if (invalidAssignment) {
        useAlert(this.$t('DDD_ASSIGNMENTS.VALIDATION.DDD_REQUIRED'));
        return;
      }

      if (this.hasDuplicateDdds()) {
        useAlert(this.$t('DDD_ASSIGNMENTS.VALIDATION.DDD_DUPLICATE'));
        return;
      }

      this.isSaving = true;
      try {
        const updates = this.assignments.map(assignment => ({
          ...assignment,
          ddd: this.normalizeDdd(assignment.ddd),
          agent_id:
            assignment.target === 'agent' ? assignment.agent_id || null : null,
          team_id:
            assignment.target === 'team' ? assignment.team_id || null : null,
        }));

        const createRequests = updates
          .filter(assignment => !assignment.id)
          .map(assignment =>
            DddAssignmentsAPI.create({
              ddd_assignment: {
                ddd: assignment.ddd,
                agent_id: assignment.agent_id,
                team_id: assignment.team_id,
              },
            })
          );

        const updateRequests = updates
          .filter(assignment => assignment.id)
          .map(assignment =>
            DddAssignmentsAPI.update(assignment.id, {
              ddd_assignment: {
                ddd: assignment.ddd,
                agent_id: assignment.agent_id,
                team_id: assignment.team_id,
              },
            })
          );

        const deleteRequests = this.removedAssignmentIds.map(id =>
          DddAssignmentsAPI.delete(id)
        );

        await Promise.all([
          ...deleteRequests,
          ...createRequests,
          ...updateRequests,
        ]);

        this.removedAssignmentIds = [];
        await this.fetchAssignments();
        useAlert(this.$t('DDD_ASSIGNMENTS.SAVE_SUCCESS'));
      } catch (error) {
        useAlert(
          error?.response?.data?.message ||
            this.$t('DDD_ASSIGNMENTS.SAVE_ERROR')
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-white dark:bg-slate-900">
    <SettingsHeader
      :header-title="$t('DDD_ASSIGNMENTS.TITLE')"
      show-back-button
      :back-button-label="$t('CONTACT_PROFILE.BACK_BUTTON')"
      :back-url="backUrl"
      :show-new-button="false"
    >
      <template #actions>
        <div class="flex items-center gap-2">
          <Button
            :label="$t('DDD_ASSIGNMENTS.ADD_ROW')"
            icon="i-lucide-plus"
            variant="outline"
            color="slate"
            size="sm"
            @click="addRow"
          />
          <Button
            :label="$t('DDD_ASSIGNMENTS.SAVE')"
            icon="i-lucide-save"
            variant="solid"
            color="blue"
            size="sm"
            :is-loading="isSaving"
            @click="saveAssignments"
          />
        </div>
      </template>
    </SettingsHeader>

    <div class="flex-1 overflow-auto p-4">
      <div class="flex min-h-full flex-col">
        <div class="w-full space-y-4 pb-4">
          <p class="text-sm text-slate-600 dark:text-slate-300">
            {{ $t('DDD_ASSIGNMENTS.DESCRIPTION') }}
          </p>

          <h2 class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{ $t('DDD_ASSIGNMENTS.CONFIGURATION_TITLE') }}
          </h2>

          <div
            v-if="isFetching"
            class="flex items-center gap-2 text-slate-600 dark:text-slate-300"
          >
            <Spinner size="" />
            <span>{{ $t('DDD_ASSIGNMENTS.LOADING') }}</span>
          </div>

          <div v-else class="space-y-3">
            <div
              v-if="assignments.length === 0"
              class="rounded-md border border-dashed border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-500"
            >
              {{ $t('DDD_ASSIGNMENTS.EMPTY_STATE') }}
            </div>

            <div
              v-if="assignments.length"
              class="hidden md:grid grid-cols-[140px_260px_260px_auto] gap-3 text-xs font-semibold uppercase text-slate-500"
            >
              <span>{{ $t('DDD_ASSIGNMENTS.DDD_LABEL') }}</span>
              <span>{{ $t('DDD_ASSIGNMENTS.TARGET_LABEL') }}</span>
              <span>{{ $t('DDD_ASSIGNMENTS.ASSIGNEE_LABEL') }}</span>
              <span class="sr-only">{{ $t('DDD_ASSIGNMENTS.REMOVE') }}</span>
            </div>

            <div
              v-for="(assignment, index) in assignments"
              :key="assignment.id || `new-${index}`"
              class="grid grid-cols-1 md:grid-cols-[140px_260px_260px_auto] gap-3 items-center"
            >
              <div class="flex items-center">
                <label
                  class="block text-xs font-medium text-slate-500 md:hidden"
                >
                  {{ $t('DDD_ASSIGNMENTS.DDD_LABEL') }}
                </label>
                <woot-input
                  v-model.trim="assignment.ddd"
                  class="w-full ddd-input"
                  type="text"
                  inputmode="numeric"
                  maxlength="2"
                  :placeholder="$t('DDD_ASSIGNMENTS.DDD_PLACEHOLDER')"
                  @input="value => onDddInput(index, value)"
                />
              </div>
              <div class="flex items-center">
                <label
                  class="block text-xs font-medium text-slate-500 md:hidden"
                >
                  {{ $t('DDD_ASSIGNMENTS.TARGET_LABEL') }}
                </label>
                <MultiselectDropdown
                  class="ddd-assignment-select"
                  :options="targetOptions"
                  :selected-item="selectedTargetFor(assignment)"
                  :has-thumbnail="false"
                  :show-search="false"
                  :multiselector-title="$t('DDD_ASSIGNMENTS.TARGET_LABEL')"
                  :multiselector-placeholder="$t('DDD_ASSIGNMENTS.CHOOSE_TARGET')"
                  :no-search-result="$t('DDD_ASSIGNMENTS.NO_RESULTS')"
                  :input-placeholder="$t('DDD_ASSIGNMENTS.SEARCH')"
                  @click="target => onSelectTarget(index, target.value)"
                />
              </div>
              <div class="flex items-center">
                <label
                  class="block text-xs font-medium text-slate-500 md:hidden"
                >
                  {{ $t('DDD_ASSIGNMENTS.ASSIGNEE_LABEL') }}
                </label>
                <template v-if="assignment.target">
                  <MultiselectDropdown
                    class="ddd-assignment-select"
                    :options="optionsForTarget(assignment)"
                    :selected-item="selectedAssigneeFor(assignment)"
                    :multiselector-title="$t('DDD_ASSIGNMENTS.ASSIGNEE_LABEL')"
                    :multiselector-placeholder="placeholderForAssignee(assignment)"
                    :show-search="false"
                    :no-search-result="$t('DDD_ASSIGNMENTS.NO_RESULTS')"
                    :input-placeholder="$t('DDD_ASSIGNMENTS.SEARCH')"
                    @click="assignee => onSelectAssignee(index, assignee)"
                  />
                </template>
                <template v-else>
                  <div
                    class="w-full rounded border border-dashed border-slate-200 px-3 py-2 text-sm text-slate-400"
                  >
                    {{ $t('DDD_ASSIGNMENTS.CHOOSE_TARGET') }}
                  </div>
                </template>
              </div>
              <div class="flex justify-end md:justify-start">
                <Button
                  v-tooltip.top="$t('DDD_ASSIGNMENTS.REMOVE')"
                  icon="i-lucide-trash-2"
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  @click="removeRow(index)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ddd-assignment-select {
  width: 100%;
}

.ddd-assignment-select::v-deep {
  margin-bottom: 0 !important;
}

.ddd-assignment-select::v-deep > .relative {
  margin-bottom: 0 !important;
  width: 100%;
}

.ddd-assignment-select::v-deep .mb-2 {
  margin-bottom: 0 !important;
}

.ddd-assignment-select::v-deep .actions-cv-select {
  width: 100% !important;
  height: 40px !important;
}

.ddd-input {
  width: 100%;
}

.ddd-input::v-deep {
  margin-bottom: 0 !important;
}

.ddd-input::v-deep input {
  margin-bottom: 0 !important;
  height: 40px !important;
}
</style>
